import JSZip from 'jszip';

/**
 * Utility class for handling ZIP file extraction
 */
export class ZipExtractor {
  /**
   * Extract ZIP file to memory and return a map of file paths to their content
   * @param zipBlob The ZIP file as a Blob
   * @param onProgress Optional callback for extraction progress
   * @returns Map of relative file paths to their content (string for text/json, Blob URL for images)
   */
  static async extractToMemory(
    zipBlob: Blob,
    onProgress?: (progress: number) => void
  ): Promise<Map<string, string | Blob>> {
    const zip = new JSZip();
    const zipContent = await zip.loadAsync(zipBlob);
    const extractedFiles = new Map<string, string | Blob>();

    const fileEntries: { relativePath: string; zipEntry: JSZip.JSZipObject }[] = [];
    zipContent.forEach((relativePath, zipEntry) => {
      if (!zipEntry.dir) {
        fileEntries.push({ relativePath, zipEntry });
      }
    });

    let extractedCount = 0;
    const totalFiles = fileEntries.length;

    for (const { relativePath, zipEntry } of fileEntries) {
      const isImage = /\.(jpg|jpeg|png|gif|bmp|webp|svg)$/i.test(relativePath);

      if (isImage) {
        const blob = await zipEntry.async('blob');
        const blobUrl = URL.createObjectURL(blob);
        extractedFiles.set(relativePath, blobUrl);
      } else if (relativePath.endsWith('.json')) {
        const text = await zipEntry.async('text');
        extractedFiles.set(relativePath, text);
      } else {
        const text = await zipEntry.async('text');
        extractedFiles.set(relativePath, text);
      }

      extractedCount++;
      if (onProgress && totalFiles > 0) {
        onProgress(Math.round((extractedCount / totalFiles) * 100));
      }
    }

    return extractedFiles;
  }

  /**
   * Clean up blob URLs from extracted files
   * @param extractedFiles Map of extracted files
   */
  static cleanupBlobUrls(extractedFiles: Map<string, string | Blob>): void {
    extractedFiles.forEach((value) => {
      if (typeof value === 'string' && value.startsWith('blob:')) {
        URL.revokeObjectURL(value);
      }
    });
    extractedFiles.clear();
  }
}

